import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  deleteToken,
  onMessage,
  Messaging,
  MessagePayload,
} from 'firebase/messaging';
import { environment } from 'src/environments/environment';

const DEFAULT_FIREBASE_CONFIG = {
  apiKey: 'AIzaSyBud5rgjrtw_O1pGFjdo6oWlgzl4Le4EQ8',
  authDomain: 'autoloop-d2808.firebaseapp.com',
  projectId: 'autoloop-d2808',
  storageBucket: 'autoloop-d2808.firebasestorage.app',
  messagingSenderId: '139257561419',
  appId: '1:139257561419:web:c99387286618a7db6aa8ce',
  measurementId: 'G-TFD9EC4DGD',
};

@Injectable({
  providedIn: 'root',
})
export class FirebaseMessagingService {
  private platformId = inject(PLATFORM_ID);
  private firebaseApp: FirebaseApp | null = null;
  private messaging: Messaging | null = null;

  private messageSource = new BehaviorSubject<MessagePayload | null>(null);
  currentMessage$ = this.messageSource.asObservable();

  private fcmTokenSubject = new BehaviorSubject<string | null>(null);
  fcmToken$ = this.fcmTokenSubject.asObservable();

  private notificationClickSource = new BehaviorSubject<{ data: any; url: string } | null>(null);
  notificationClick$ = this.notificationClickSource.asObservable();

  constructor() {
    this.initFirebase();
    this.listenToServiceWorkerMessages();
  }

  private listenToServiceWorkerMessages(): void {
    if (isPlatformBrowser(this.platformId) && 'serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'NOTIFICATION_CLICK') {
          this.notificationClickSource.next(event.data);
        }
      });
    }
  }

  private async initFirebase(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const config =
        (environment as any).firebaseConfig || DEFAULT_FIREBASE_CONFIG;
      if (config && config.apiKey) {
        if (!getApps().length) {
          this.firebaseApp = initializeApp(config);
        } else {
          this.firebaseApp = getApps()[0];
        }

        if (this.firebaseApp) {
          this.messaging = getMessaging(this.firebaseApp);
          this.listenForForegroundMessages();
        }
      }
    } catch (error) {}
  }

  async requestPermission(vapidKey?: string): Promise<string | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      return null;
    }

    try {
      await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      const registration = await navigator.serviceWorker.ready;

      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission === 'granted') {
        if (!this.firebaseApp) {
          await this.initFirebase();
        }
        if (!this.messaging && this.firebaseApp) {
          this.messaging = getMessaging(this.firebaseApp);
          this.listenForForegroundMessages();
        }

        if (this.messaging) {
          const tokenOptions: {
            serviceWorkerRegistration: ServiceWorkerRegistration;
            vapidKey?: string;
          } = {
            serviceWorkerRegistration: registration,
          };

          const key =
            vapidKey ||
            (environment as any).firebaseConfig?.vapidKey ||
            (DEFAULT_FIREBASE_CONFIG as any)?.vapidKey;

          if (key) {
            tokenOptions.vapidKey = key;
          }

          const currentToken = await getToken(this.messaging, tokenOptions);
          if (currentToken) {
            this.fcmTokenSubject.next(currentToken);
            return currentToken;
          }
        }
      }
    } catch {}

    return null;
  }

  async deleteToken(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      if (!this.messaging && this.firebaseApp) {
        this.messaging = getMessaging(this.firebaseApp);
      }

      if (this.messaging) {
        const deleted = await deleteToken(this.messaging);
        this.fcmTokenSubject.next(null);
        return deleted;
      }
    } catch {}

    return false;
  }

  private listenForForegroundMessages(): void {
    if (!this.messaging) return;

    onMessage(this.messaging, (payload: MessagePayload) => {
      this.messageSource.next(payload);
    });
  }
}
